import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Checkbox, TablePagination } from '@material-ui/core';
import { colors } from '@/styles';
import { toMenuActions } from './buttons';
import { applyAutoExpand, applyUserToggle, columnKey } from './gridOps';
import ColumnHeader from './ColumnHeader';
import GridChildRow from './ChildRow';
import {
    LineRow,
    SelectCell,
    KebabCell,
    Chevron,
    ChildPanel,
    ChildDivider,
    ChildDividerLabel,
    ChildTable,
    ChildHeadRow,
    Footnote,
} from './blocks';
import RowMenu from './RowMenu';

const GreenCheckbox = (props) => (
    <Checkbox size="small" style={{ padding: 0, color: colors.greens.primary }} {...props} />
);

// One parent row + its collapsible child panel. Parent cells come from `parentColumns[].render(row)`
// (each render returns exactly one grid cell — e.g. the segmented status bar); the grid owns only the
// select checkbox, the expand chevron, and the kebab.
const GridParentRow = ({
    subject,
    row,
    parentColumns,
    parentGridTemplate,
    childColumns,
    childGridTemplate,
    getChildren,
    getChildId,
    selectable,
    selected,
    onToggleChild,
    onToggleRow,
    parentActions,
    rowActions,
    childPanelLabel,
    childFootnote,
    columnOps = false,
    childSort = null,
    setChildSort,
    childFilters = {},
    setChildFilters,
    childFilterOptions = {},
    resizable = false,
    childWidths = {},
    setChildWidths,
    paginated = false,
    childPageSize,
    childPageOptions,
    autoExpand = false,
    searchHint = null,
    searchTerm = '',
}) => {
    const [expanded, setExpanded] = useState(false);
    const expandedRef = useRef(false);
    const openedBySearch = useRef(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(childPageSize);

    const commitExpanded = (next) => {
        expandedRef.current = next.expanded;
        openedBySearch.current = next.openedBySearch;
        setExpanded(next.expanded);
    };

    // Only `autoExpand` retriggers this. A chevron click takes ownership and must not re-enter it.
    useEffect(() => {
        commitExpanded(
            applyAutoExpand({
                expanded: expandedRef.current,
                openedBySearch: openedBySearch.current,
                autoExpand,
            })
        );
        // commitExpanded reads refs and setState. Listing it would re-open a row the user closed.
    }, [autoExpand]);

    // Child ops are grid-global, so a change there reshuffles this row's set too.
    useEffect(() => setPage(0), [childSort, childFilters, autoExpand, searchTerm]);

    const children = getChildren(row) || [];
    const total = children.length;
    // Clamped so a shrunk set can't leave us on a blank page after the pager unmounts.
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(page, pageCount - 1);
    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);
    const visible = paginated ? children.slice(safePage * pageSize, safePage * pageSize + pageSize) : children;

    const selectedInRow = children.filter((child) => selected[getChildId(child)]).length;
    const allSelected = total > 0 && selectedInRow === total;
    const someSelected = selectedInRow > 0 && !allSelected;

    const parentMenuActions = toMenuActions(parentActions, { subject, row });

    return (
        <div>
            <LineRow columns={parentGridTemplate}>
                <SelectCell>
                    {selectable ? (
                        <GreenCheckbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={() => onToggleRow(row)}
                        />
                    ) : null}
                </SelectCell>
                <Chevron
                    open={expanded}
                    onClick={() => commitExpanded(applyUserToggle({ expanded: expandedRef.current }))}
                >
                    ▶
                </Chevron>
                {parentColumns.map((column) => (
                    <React.Fragment key={column.key || column.header}>
                        {column.render(row, { expanded, searchHint })}
                    </React.Fragment>
                ))}
                <KebabCell>{parentMenuActions.length ? <RowMenu actions={parentMenuActions} /> : null}</KebabCell>
            </LineRow>
            <Collapse in={expanded} unmountOnExit>
                <ChildPanel>
                    {childPanelLabel ? (
                        <ChildDivider>
                            <ChildDividerLabel>{childPanelLabel}</ChildDividerLabel>
                            <span />
                        </ChildDivider>
                    ) : null}
                    <ChildTable>
                        <ChildHeadRow columns={childGridTemplate}>
                            <SelectCell>
                                {selectable ? (
                                    <GreenCheckbox
                                        checked={allSelected}
                                        indeterminate={someSelected}
                                        onChange={() => onToggleRow(row)}
                                    />
                                ) : null}
                            </SelectCell>
                            {childColumns.map((column) => (
                                <ColumnHeader
                                    key={columnKey(column)}
                                    column={column}
                                    columnOps={columnOps}
                                    sortDir={childSort?.key === columnKey(column) ? childSort.dir : null}
                                    onSort={(dir) => setChildSort(dir ? { key: columnKey(column), dir } : null)}
                                    filterOptions={childFilterOptions[columnKey(column)]}
                                    filterValues={childFilters[columnKey(column)] || []}
                                    onFilter={(values) =>
                                        setChildFilters((prev) => ({ ...prev, [columnKey(column)]: values }))
                                    }
                                    resizable={resizable}
                                    onResize={(px) => setChildWidths((prev) => ({ ...prev, [columnKey(column)]: px }))}
                                />
                            ))}
                            <span />
                        </ChildHeadRow>
                        {visible.map((child) => (
                            <GridChildRow
                                key={getChildId(child)}
                                row={row}
                                child={child}
                                columns={childColumns}
                                gridTemplate={childGridTemplate}
                                selectable={selectable}
                                selected={!!selected[getChildId(child)]}
                                onToggle={() => onToggleChild(getChildId(child))}
                                menuActions={toMenuActions(rowActions, { subject, row, child })}
                            />
                        ))}
                    </ChildTable>
                    {paginated && total > pageSize ? (
                        <TablePagination
                            component="div"
                            count={total}
                            page={safePage}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={childPageOptions}
                            labelRowsPerPage="Items per page"
                            onChangePage={(_, next) => setPage(next)}
                            onChangeRowsPerPage={(e) => {
                                setPageSize(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    ) : null}
                    {childFootnote ? <Footnote>{childFootnote}</Footnote> : null}
                </ChildPanel>
            </Collapse>
        </div>
    );
};

export default GridParentRow;
